// GENERATED — DO NOT HAND-EDIT.
//
// Source of truth: specs/tier-05-experience/pledge.entity-hint.yaml
// Generator: tools/v2-gen/v2gen.py
// Regenerate via: make v2-generate-screens
//
// Hand-edits to non-override-block content WILL be lost on next
// regeneration. See specs/_product/dashboard-v2-page-generator.md §5
// for override blocks (imports / helpers / actions / custom-tabs).

import { createElement } from "react";
import type { DetailPageConfig } from "../../components/composition/DetailPage";
import { StageChip } from "../../components/composition/StageChip";
import { pledgesApi } from "../../api/pledges";
import type { Pledge } from "../../api/types";
// override-block:imports BEGIN
import { ProgressBar } from "../../components/composition/ProgressBar";
import { ACISurface } from "../../components/aci/ACISurface";
import type { PledgeStatus, PledgeFrequency } from "../../api/types";
// CHG-1294 — per-page RBAC gate wiring (consumes GATE_CATALOG).
import { GATE_CATALOG } from "../../auth/gateCatalog";
import { toGate } from "../../auth/toGateProp";
// CHG-1325 — Tag taxonomy phase 3: Tags tab via EntityTagsSection.
import { EntityTagsSection } from "../../components/composition/EntityTagsSection";
// override-block:imports END

// override-block:helpers BEGIN
const STATUS_LABEL: Record<PledgeStatus, string> = {
  committed: "Committed",
  in_progress: "In progress",
  fulfilled: "Fulfilled",
  lapsed: "Lapsed",
  canceled: "Canceled",
};
const FREQUENCY_LABEL: Record<PledgeFrequency, string> = {
  one_time: "One-time",
  weekly: "Weekly",
  biweekly: "Bi-weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annual: "Annual",
};

function donorLabel(p: Pledge): string {
  if (p.donor_name) return p.donor_name;
  if (p.donor_email) return p.donor_email;
  return p.donor_id ? p.donor_id.slice(0, 8) + "…" : "Unknown donor";
}

// DRIFT-1173 (CHG-3054) — PLG-R03 reminder-status labels (scheduled / due /
// inactive) for the Schedule-tab "Reminder cadence" section.
const REMINDER_STATUS_LABEL: Record<string, string> = {
  scheduled: "Scheduled",
  due: "Due now",
  inactive: "Inactive",
};

// DRIFT-1173 (CHG-3054) — staff-side "Reminder cadence" card. Surfaces the
// admin PledgeRead.reminder_cadence (PLG-R03, CHG-3035): the operative ACI
// schedule the C3 reminder cron fires on — schedule_label / reminder_status
// plus next/last reminder timestamps. Parity with the donor-facing
// MyPledgeDetail "Reminder schedule" card (CHG-3048). Reminders never charge.
function reminderCadenceCard(
  p: Pledge,
  fmt: {
    formatDate: (
      value: string,
      style?: "medium" | "relative" | "short" | "long",
    ) => string;
  },
): ReturnType<typeof createElement> | null {
  const rc = p.reminder_cadence;
  if (!rc) return null;
  const cadence =
    rc.schedule_label && rc.schedule_label.trim() ? rc.schedule_label : "—";
  const statusLabel = REMINDER_STATUS_LABEL[rc.reminder_status] ?? rc.reminder_status;
  const nextAt = rc.next_reminder_at ? fmt.formatDate(rc.next_reminder_at, "medium") : "—";
  const lastAt = rc.last_reminder_at ? fmt.formatDate(rc.last_reminder_at, "medium") : "—";
  return createElement(
    "div",
    {
      className: "card",
      style: { padding: 16, display: "flex", flexDirection: "column", gap: 12 },
    },
    createElement("div", { style: { fontWeight: 600, fontSize: 14 } }, "Reminder cadence"),
    createElement(
      "div",
      {
        style: {
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 12,
          fontSize: 13,
        },
      },
      createElement(
        "div",
        null,
        "Cadence: ",
        createElement("span", { style: { textTransform: "capitalize" } }, cadence),
      ),
      createElement("div", null, "Status: ", statusLabel),
      createElement("div", null, "Next reminder: ", nextAt),
      createElement("div", null, "Last reminder: ", lastAt),
    ),
    createElement(
      "div",
      { style: { fontSize: 12, color: "var(--color-text-muted)", lineHeight: 1.5 } },
      "Reminders are notifications only — they never charge the donor. Each gift is completed through a secure checkout link.",
    ),
  );
}
// override-block:helpers END

export const pledgeDetailConfig: DetailPageConfig<Pledge> = {
  idParam: "pledgeId",
  queryKey: (id: string) => ["pledges", "detail", id] as const,
  queryFn: (id: string) => pledgesApi.getPledge(id),
  backTo: "/pledges",
  // override-block:title BEGIN
  // Title synthesis: entity-hint declares title_field=total_amount_minor_units
  // (bare cents number = poor title); slot synthesizes "$1,000.00 USD pledge"
  // using local formatting. Pattern shared with recurring-donation + donation.
  title: (p) => {
    const amountStr = `${(p.total_amount_minor_units / 100).toFixed(2)} ${p.currency}`;
    return `${amountStr} pledge`;
  },
  // override-block:title END
  // override-block:subtitle BEGIN
  subtitle: (p) => p.campaign_name ?? donorLabel(p),
  // override-block:subtitle END
  stageChip: (r) =>
    createElement(StageChip, {
      stage_id: r.stage,
      entity: "pledge",
      size: "lg",
    }),
  statusCards: (r, _fmt) => [
    {
      id: "total",
      label: "Total",
      value: r.total_amount_minor_units,
      format: "currency",
      currency: r.currency,
      tone: "g-giving",
    },
    {
      id: "fulfilled",
      label: "Fulfilled",
      value: r.fulfilled_minor_units,
      format: "currency",
      currency: r.currency,
      tone: "g-giving",
    },
    {
      id: "outstanding",
      label: "Outstanding",
      value: r.outstanding_minor_units,
      format: "currency",
      currency: r.currency,
      tone: "g-events",
    },
    // override-block:statcard-due BEGIN
    // Custom render: due_date may be null; relative-date formatter chokes on
    // null. Switch format to text + supply a render that picks formatDate or
    // a "—" sentinel. Same pattern as asset-detail.last_used.
    {
      id: "due",
      label: "Due",
      value: r.due_date ?? null,
      format: r.due_date ? "date" : "text",
      tone: "g-mygiving",
      render: r.due_date
        ? createElement("span", { className: "tabular" }, _fmt.formatDate(r.due_date, "relative"))
        : createElement("span", { style: { color: "var(--color-text-muted)" } }, "—"),
    },
    // override-block:statcard-due END
  ],
  actions: (_r) => [
    // override-block:actions BEGIN
    { id: "record-payment", label: "Record payment", variant: "primary", permissionGate: toGate(GATE_CATALOG.pledge_detail["record-payment"], "hide") },
    { id: "send-reminder", label: "Send reminder", variant: "secondary", permissionGate: toGate(GATE_CATALOG.pledge_detail["send-reminder"], "disable") },
    { id: "edit", label: "Edit", variant: "ghost", permissionGate: toGate(GATE_CATALOG.pledge_detail["edit"], "disable") },
    // override-block:actions END
  ],
  tabs: [
    // override-block:custom-tabs BEGIN
    // 4 tabs per pledge-detail-v2.cross-screen.yaml §layout.tabs + CHG-1325
    // Tags tab. Overview tab uses ProgressBar (CHG-1236) for fulfillment
    // progress visualization (3rd ProgressBar consumer overall). Remaining
    // tabs are placeholder bodies citing their fetch endpoints until ε CHGs
    // flesh them out.
    {
      id: "overview",
      label: "Overview",
      render: (p, fmt) => {
        const totalStr = fmt.formatCurrency(p.total_amount_minor_units, p.currency);
        const fulfilledStr = fmt.formatCurrency(p.fulfilled_minor_units, p.currency);
        return createElement(
          "div",
          { style: { display: "flex", flexDirection: "column", gap: 16 } },
          // Fulfillment progress — the headline signal for a pledge.
          createElement(
            "div",
            { className: "card", style: { padding: 16 } },
            createElement(ProgressBar, {
              value: p.fulfilled_minor_units,
              max: p.total_amount_minor_units,
              tone: "g-giving",
              label: "Fulfillment progress",
              valueFormatted: `${fulfilledStr} of ${totalStr}`,
              height: 12,
            }),
          ),
          // Key facts card
          createElement(
            "div",
            { className: "card", style: { padding: 16, fontSize: 13, lineHeight: 1.6 } },
            createElement("div", null, "Donor: ", donorLabel(p)),
            createElement(
              "div",
              null,
              "Frequency: ",
              FREQUENCY_LABEL[p.frequency] ?? p.frequency,
            ),
            createElement(
              "div",
              null,
              "Status: ",
              STATUS_LABEL[p.status] ?? p.status,
            ),
            createElement(
              "div",
              null,
              "Committed: ",
              fmt.formatDate(p.created_at, "long"),
            ),
            createElement(
              "div",
              null,
              "Due: ",
              p.due_date ? fmt.formatDate(p.due_date, "long") : "—",
            ),
            p.reminder_sent_at
              ? createElement(
                  "div",
                  { style: { marginTop: 8, color: "var(--color-text-muted)" } },
                  "Last reminder sent: ",
                  fmt.formatDate(p.reminder_sent_at, "relative"),
                )
              : null,
            p.campaign_name
              ? createElement(
                  "div",
                  { style: { color: "var(--color-text-muted)" } },
                  "Campaign: ",
                  p.campaign_name,
                )
              : null,
          ),
        );
      },
    },
    {
      id: "payments",
      label: "Payments",
      render: (p) =>
        createElement(
          "div",
          { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
          `Payment history — fetched via GET /pledges/${p.id}/payments. ` +
            "Each PledgePayment row carries amount + donation_id (when linked) + " +
            "paid_at + channel. Full UI in ε CHG.",
        ),
    },
    {
      id: "schedule",
      label: "Schedule",
      render: (p, fmt) =>
        createElement(
          "div",
          { style: { display: "flex", flexDirection: "column", gap: 16 } },
          // DRIFT-1173 (CHG-3054) — PLG-R03 reminder-cadence section (the
          // operative ACI reminder schedule, distinct from the installment
          // projection below). Returns null when no cadence is present.
          reminderCadenceCard(p, fmt),
          createElement(
            "div",
            { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
            `Projected schedule — fetched via GET /pledges/${p.id}/timeline?kind=schedule ` +
              "(bridge; canonical /pledges/{id}/schedule endpoint pending). " +
              "Next N scheduled payment dates with amounts. Full UI in ε CHG.",
          ),
          // CHG-1274 — ACI runtime surface 5 (pledge-schedule-rationale).
          createElement(ACISurface, {
            surface_id: "pledge-schedule-rationale",
            entity_id: p.id,
          }),
        ),
    },
    {
      id: "communications",
      label: "Communications",
      render: (p) =>
        createElement(
          "div",
          { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
          `Reminder + thank-you delivery logs — fetched via ` +
            `GET /pledges/${p.id}/timeline?kind=communications (bridge; canonical ` +
            "/pledges/{id}/delivery-logs endpoint pending). Full UI in ε CHG.",
        ),
    },
    // CHG-1325 — Tag taxonomy phase 3. Tags applied to this pledge.
    {
      id: "tags",
      label: "Tags",
      render: (p) =>
        createElement(EntityTagsSection, {
          entityType: "pledge",
          entityId: p.id,
        }),
    },
    // override-block:custom-tabs END
  ],
};
